<template>
  <div
    v-if="sid"
    v-analytics.pageview="'answer'"
    class="page page-answer"
  >
    <answer-header />
    <div class="answer">
      <div
        v-if="pages[pageIndex] && pages[pageIndex].questions"
        class="answer-container"
      >
        <question
          v-for="question in currentPageQuestionIds(pages[pageIndex].questions)"
          :key="question.id"
          :item="question"
          :errors="errors[question.id]"
          @answer="onAnswer"
        />
      </div>
      <div class="answer-control">
        <div class="answer-btn-wrap">
          <b-button
            v-if="pageIndex > 0 && configs.isCanFallback"
            type="is-primary"
            class="answer-btn"
            @click="setPage('prev')"
          >
            <i class="answer-arrow answer-arrow-left" />
          </b-button>
          <b-button
            v-if="!isEndPage"
            type="is-primary"
            class="answer-btn"
            @click="checkPageDown"
          >
            <i class="answer-arrow answer-arrow-right" />
          </b-button>
          <b-button
            v-if="isEndPage"
            v-analytics.click="{
              action: 'submit',
              category: 'click',
            }"
            type="is-primary"
            class="answer-btn"
            @click="submit({ abnormalEnd: surveyAbnormalEnd })"
          >
            {{ $t('submit') }}
          </b-button>
        </div>
      </div>
    </div>
    <input-modal />
  </div>
</template>

<script>
import {
  mapState, mapGetters, mapMutations, mapActions,
} from 'vuex';
import Raven from 'raven-js';
import { forEach, has } from 'lodash-es';
import { question, inputModal } from '@/components';
import * as types from '@/stores/mutation-types';
import { submitAnswer } from '@/apis/survey';
import { getClientId } from '@/apis/report';
import baseValidator from '@/utils/questions/validators/base';
import validatorFactory from '@/utils/questions/validators/extends';
import displayValidator from '@/utils/questions/validators/displayRules';
import {
  filterSubmitQuery, getUrlParam, useNewExpression, questionIsVisible,
} from '@/utils/helper';
import { recordPageShow, recordAnswer, recordSubmit } from '@/utils/answerAnalyze';
import Cache from 'web-cache-facade';
import { isMatrix, isMatrixRange, isSort } from '@/utils/questions/questionHelper';
import AnswerHeader from './answer-header.vue';
import {
  formatAnswers, getUserFeatures, hasUserFeatures, getAnswerHistoryCount,
} from './answer-utils';
import answerMixin from './answer-mixin';

export default {
  name: 'Answer',

  components: {
    question,
    inputModal,
    AnswerHeader,
  },

  mixins: [answerMixin],

  data() {
    return {
      sid: '',
      errors: {},
      isRefresh: false,
    };
  },

  computed: {
    ...mapGetters([
      'isEndPage',
      'progressQuestionIds',
      'questionIds',
    ]),
    ...mapState([
      'pages',
      'pageIndex',
      'endPageIndex',
      'questions',
      'answers',
      'configs',
      'surveyData',
      'isPreview',
      'surveyEnd',
      'surveyAbnormalEnd',
      'groups',
    ]),
  },

  watch: {
    pageIndex: {
      handler(val) {
        recordPageShow(val);
        window.scrollTo(0, 0);
      },
      immediate: true,
    },
    surveyData(val) {
      const { _id: sid } = val;
      this.sid = sid;
    },

    configs: {
      handler(val) {
        if (Object.keys(val).length > 0) {
          if (!val.isSaveAnswersHistory) {
            this.$store.commit(types.SURVEY_RESET_STATE);
          }

          if (val.isSaveAnswersHistory && this.isRefresh && getAnswerHistoryCount(this.answers) > 0) {
            this.routeReplace('answer-history', this.$route.query, this.getSurveyLang(this.surveyData));
            return;
          }
        }

        // 海外验证流程增加数据校验，功能开启时，当答题者answer页检查国家/地区、年龄数据是否有值；若为空，跳转到海外流程页面
        this.userFeatures = getUserFeatures();
        if (val.verification && !hasUserFeatures(this.userFeatures)) {
          this.routeReplace('verification', this.$route.query, this.getSurveyLang(this.surveyData));
        }
      },
      immediate: true,
    },

  },

  beforeRouteEnter(to, from, next) {
    const tenSeconds = 1000 * 10;
    if (!from.name || from.name === 'answer-history') {
      Cache.store('localStorage').put('isRecordPV', false, tenSeconds);
    } else {
      Cache.store('localStorage').put('isRecordPV', true, tenSeconds);
    }

    next((vm) => {
      if (!from.name) {
        // eslint-disable-next-line no-param-reassign
        vm.isRefresh = true;
      }
      vm.showAnswers();
    });
  },

  created() {
    const { _id: sid } = this.surveyData;

    if (Object.keys(this.configs).length > 0) {
      if (!this.configs.isSaveAnswersHistory) {
        this.$store.commit(types.SURVEY_RESET_STATE);
      }
    }

    this.sid = sid;
    this.showDisplayRuleAnswer();
  },

  methods: {
    showAnswers() {
      if (this.configs.isSaveAnswersHistory && this.isRefresh
      && getAnswerHistoryCount(this.answers) > 0) {
        this.routeReplace('answer-history', this.$route.query, this.getSurveyLang(this.surveyData));
        return;
      }

      // 海外验证流程增加数据校验，功能开启时，当答题者answer页检查国家/地区、年龄数据是否有值；若为空，跳转到海外流程页面
      this.userFeatures = getUserFeatures();
      if (this.configs.verification && !hasUserFeatures(this.userFeatures)) {
        this.routeReplace('verification', this.$route.query, this.getSurveyLang(this.surveyData));
      }
    },

    showDisplayRuleAnswer() {
      if (getAnswerHistoryCount(this.answers) > 0) {
        const eventKey = this.getEventKey();
        this.displayChange(eventKey);
      }
    },

    getEventKey() {
      const eventKey = [];
      forEach(this.answers, (item) => {
        if (item) {
          if (isMatrix(item) && !isMatrixRange(item)) {
            const { groups } = item;
            if (groups) {
              const groupKeys = Object.keys(groups);
              for (let i = 0; i < groupKeys.length; i++) {
                if (groups[groupKeys[i]].length > 0) {
                  eventKey.push(`${item.id}_${groupKeys[i]}`);
                }
              }
            }
          } else if (isSort(item) || isMatrixRange(item)) {
            const { groups } = item;
            const groupKeys = Object.keys(groups);
            for (let i = 0; i < groupKeys.length; i++) {
              eventKey.push(`${item.id}_${groupKeys[i]}`);
            }
          } else {
            eventKey.push(item?.id);
          }

          if (eventKey.length) {
            this.quoteChange(eventKey[eventKey.length - 1]);
          }
          this.relateChange({ questionId: item.id });
        }
      });
      return eventKey;
    },

    ...mapActions([
      'relateChange',
      'displayChange',
      'displayChangeByNew',
      'quoteChange',
      'resetState',
      'setPage',
    ]),

    ...mapMutations({
      answerOne: types.ANSWER_ONE,
    }),

    currentPageQuestionIds(questionIds) {
      if (questionIds) {
        return questionIds.map(id => this.questions[id])
          .filter((q) => {
            if (useNewExpression()) {
              let atleastSubtitleShow = true;
              let atleastOptionShow = true;

              if (q.options) {
                // 如果有选项，则至少要一个选项显示或者关联的选项显示
                atleastOptionShow = this.answers[q.relatedBy] || q.options?.length;
              }

              if (q.subTitles) {
                // 至少需要一个子问题显示或者有设置问题关联，才显示整题
                atleastSubtitleShow = this.answers[q.questionRelateBy]
                  || q.subTitles.some(subTitle => subTitle.visible);
              }
              return questionIsVisible(q, this.groups) && atleastSubtitleShow && atleastOptionShow;
            }

            return !q.hidden;
          });
      }

      return [];
    },

    async onAnswer({
      questionId, values, subTitleId, sortGroup,
    }) {
      this.clearErrors(questionId);
      this.answerOne({ questionId, values });
      const eventKey = subTitleId ? `${questionId}_${subTitleId}` : questionId;

      // blank的变化不会影响选项关联和显示逻辑
      if (values.key !== 'blank') {
        await this.quoteChange(eventKey);
        if (useNewExpression()) {
          // 使用新规则解析，不在vuex中计算选项关联，直接在question中计算
          this.displayChangeByNew();
        } else {
          let eventKeys = await this.relateChange({ questionId, subTitleId });
          // 关联选项的变化会影响后面的依赖的显示逻辑
          eventKeys.push(eventKey);
          if (sortGroup && sortGroup.length) {
            eventKeys = eventKeys.concat(sortGroup);
          }
          await this.displayChange(eventKeys);
        }
      }
      recordAnswer(questionId);
    },

    setErrors(id, error) {
      this.$set(this.errors, id, error);
    },

    clearErrors(id) {
      if (id) {
        this.$set(this.errors, id, null);
      }
    },

    valid() {
      const currentPageQuestions = this.pages[this.pageIndex]?.questions;

      // 如果当前页没有题目，则不校验
      if (!currentPageQuestions) {
        return true;
      }
      const questions = this.currentPageQuestionIds(currentPageQuestions).filter(q => q?.type !== 'information');

      // eslint-disable-next-line no-labels,no-restricted-syntax,no-unused-vars
      outer:for (const q of questions) {
        if (!baseValidator.shouldKeep(q)) {
          if (q.required) {
            try {
              baseValidator.required(this.answers[q.id], q);
            } catch (e) {
              this.setErrors(q.id, {
                questionErrMsg: e.message,
                subTitleId: e.params?.subTitleId,
              });
              break;
            }
          }

          if (q.blank && has(q, 'options')) {
            // eslint-disable-next-line no-restricted-syntax,no-unused-vars
            for (const option of q.options) {
              if (option.blankRequired) {
                try {
                  baseValidator.blankRequired(this.answers[q.id], q, option.id);
                } catch (e) {
                  this.setErrors(q.id, {
                    optionId: option.id,
                    optionErrMsg: e.message,
                    subTitleId: e.params?.subTitleId,
                  });
                  // eslint-disable-next-line no-labels
                  break outer;
                }
              }
            }
          }
        }

        // 自定义针对题目类型异常，在 @/utils/validators/extends 下定义
        if (q?.type && validatorFactory[q.type]) {
          const questionValidator = validatorFactory[q.type];
          try {
            questionValidator.valid(q, this.answers[q.id], this.answers);
          } catch (e) {
            this.setErrors(q.id, { questionErrMsg: e.message });
            break;
          }
        }

        baseValidator.format(this.answers[q.id], this.sid);
      }

      return questions.every(q => !this.errors[q.id]);
    },

    checkPageDown() {
      if (this.valid()) {
        if (this.surveyEnd && this.pageIndex >= this.endPageIndex) {
          this.submit();
          return;
        }

        if (this.surveyAbnormalEnd && this.pageIndex >= this.endPageIndex) {
          this.submit({ abnormalEnd: true });
          return;
        }

        this.setPage('next');
      }
    },

    goToEndPage() {
      this.resetState();
      if (this.configs.redirect && this.configs.redirectUri) {
        const url = this.configs.redirectUri.includes('?')
          ? `${this.configs.redirectUri}&${document.location.search.slice(1)}`
          : `${this.configs.redirectUri}?${document.location.search.slice(1)}`;

        document.location.replace(url);
      } else {
        const { query } = this.$route;
        this.$router.replace({ name: 'end', query });
      }
    },

    async submit({ abnormalEnd = false } = {}) {
      if (this.valid()) {
        const result = await displayValidator(this.pages, this.questions, this.answers, this.surveyData.displayRules);
        if (!result.isPass) {
          Raven.setTagsContext({
            api: 'display_validator',
            sid: this.sid,
          });
          Raven.setExtraContext({
            expected: result.expected,
            received: result.received,
            cookie: document.cookie,
          });
          Raven.captureMessage('答题数据不匹配');
        }

        if (this.isPreview) {
          this.goToEndPage();
          return;
        }

        const loading = this.$buefy.loading.open();
        const clientId = await getClientId();
        let payload = {
          client_id: clientId,
          time: this.surveyData.time,
          callback_params: getUrlParam('callback_params'),
          channel: getUrlParam('channel'),
          query_string: filterSubmitQuery(this.$route.query),
          uid: getUrlParam('uid'),
        };
        if (+this.$route.query.callback) {
          payload.callback = +getUrlParam('callback');
        }

        if (abnormalEnd) {
          payload = {
            ...payload,
            abnormal_end: true,
          };
        } else {
          payload = {
            ...payload,
            ...this.userFeatures,
            answers: formatAnswers(this.answers, this.questions),
          };
        }

        payload.abnormal_logic = !result.isPass;

        try {
          const res = await submitAnswer(this.sid, payload);
          recordSubmit();

          if (res?.data?.id) {
            this.goToEndPage();
          }
        } catch (error) {
          this.$buefy.dialog.alert('提交失败，请刷新后试试～');
          if (error?.error?.code === 10007) {
            return;
          }
          Raven.setTagsContext({
            api: 'submit_answer',
            sid: this.sid,
          });
          Raven.setExtraContext({
            payload,
            error,
            cookie: document.cookie,
          });
          Raven.captureMessage('提交失败');
        } finally {
          loading.close();
        }
      }
    },
  },
};
</script>

<style lang="scss" src="./answers.scss"></style>
